import type { TFunction } from 'i18next';

/**
 * Knockout-bracket placeholder team-name localiser.
 *
 * Compact machine-readable codes feed in via the synthetic match objects
 * emitted by `data/injectKnockoutBracket` (or — in future — by the backend
 * via `matches.team1_placeholder` / `team2_placeholder`). We localise the
 * descriptive variants at render time and leave the universal compact
 * tokens verbatim.
 *
 * Pattern catalog (exhaustive):
 *
 *   1. `^[123][A-L]+$`                        e.g. "1A", "2B", "3ABCDF"
 *      Group-position token.
 *      - "1A"      = winner of Group A
 *      - "2B"      = runner-up of Group B
 *      - "3ABCDF"  = best third-placed team from one of A/B/C/D/F
 *                    (FIFA "best 8 thirds" lookup)
 *      Rendered VERBATIM in both EN and RU — these are universal compact
 *      codes that are already language-neutral.
 *
 *   2. `^[123][A-L]+/[123][A-L]+$`            e.g. "1A/2B", "1E/3ABCDF"
 *      R16 slot — winner of the R32 match between the two compact tokens.
 *      Rendered VERBATIM.
 *
 *   3. `^Winner R16-(\d+)$`                   QF slot — winner of R16 match #N.
 *   4. `^Winner QF-(\d+)$`                    SF slot — winner of QF match #N.
 *   5. `^Winner SF-(\d+)$`                    Final slot — winner of SF match #N.
 *   6. `^Loser SF-(\d+)$`                     3rd-place match — loser of SF #N.
 *
 * Anything else passes through verbatim (fail-soft) so a future
 * backend pattern degrades to readable English instead of breaking the UI.
 */

export type PlaceholderKind =
  | 'groupToken'
  | 'compositeToken'
  | 'winnerR16'
  | 'winnerQf'
  | 'winnerSf'
  | 'loserSf';

export interface ParsedPlaceholder {
  kind: PlaceholderKind;
  /** Raw captured value — full token for group/composite, numeric string for Winner/Loser. */
  param: string;
}

interface PlaceholderPattern {
  regex: RegExp;
  kind: PlaceholderKind;
  /**
   * Render function. Receives `t` and the regex match groups.
   * Group-token patterns return their input verbatim; localised
   * patterns delegate to `t(...)` with `{{n}}` interpolation.
   */
  render: (t: TFunction, match: RegExpMatchArray) => string;
}

const PATTERNS: PlaceholderPattern[] = [
  // Group-position token: "1A", "2B", "3ABCDF" — verbatim passthrough.
  {
    regex: /^[123][A-L]+$/,
    kind: 'groupToken',
    render: (_t, match) => match[0],
  },
  // Composite R32 token: "1A/2B", "1E/3ABCDF" — verbatim passthrough.
  {
    regex: /^[123][A-L]+\/[123][A-L]+$/,
    kind: 'compositeToken',
    render: (_t, match) => match[0],
  },
  {
    regex: /^Winner R16-(\d+)$/,
    kind: 'winnerR16',
    render: (t, match) => t('tournament:placeholder.winnerR16', { n: match[1] }) as string,
  },
  {
    regex: /^Winner QF-(\d+)$/,
    kind: 'winnerQf',
    render: (t, match) => t('tournament:placeholder.winnerQf', { n: match[1] }) as string,
  },
  {
    regex: /^Winner SF-(\d+)$/,
    kind: 'winnerSf',
    render: (t, match) => t('tournament:placeholder.winnerSf', { n: match[1] }) as string,
  },
  {
    regex: /^Loser SF-(\d+)$/,
    kind: 'loserSf',
    render: (t, match) => t('tournament:placeholder.loserSf', { n: match[1] }) as string,
  },
];

/**
 * Parse a raw backend placeholder string into a structured form.
 * Useful for tests / analytics. Returns `null` when no pattern matches.
 *
 * - Group / composite tokens: `param` is the full matched token
 *   (e.g. `"3ABCDF"`, `"1A/2B"`).
 * - `Winner R16-N` / `Winner QF-N` / `Winner SF-N` / `Loser SF-N`:
 *   `param` is the numeric string (e.g. `"1"`, `"8"`).
 */
export function parsePlaceholderTeam(raw: string | null | undefined): ParsedPlaceholder | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  for (const pattern of PATTERNS) {
    const match = trimmed.match(pattern.regex);
    if (match) {
      const param =
        pattern.kind === 'groupToken' || pattern.kind === 'compositeToken'
          ? match[0]
          : match[1];
      return { kind: pattern.kind, param };
    }
  }
  return null;
}

/**
 * Render-time localiser. Pure function — pass `t` from `useTranslation('tournament')`
 * at the call site (no hook inside).
 *
 * - Empty / null / undefined input → `''` (caller decides how to fall back further).
 * - Group / composite tokens → returned verbatim (slot-identity-first: the
 *   token IS the team identity until the bracket resolves).
 * - `Winner R16/QF/SF-N`, `Loser SF-N` → localised via i18n.
 * - Unrecognised pattern → raw trimmed string (fail-soft).
 */
export function formatPlaceholderTeam(
  raw: string | null | undefined,
  t: TFunction,
): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';

  for (const pattern of PATTERNS) {
    const match = trimmed.match(pattern.regex);
    if (match) {
      return pattern.render(t, match);
    }
  }

  // Fail-soft: unknown pattern → return the original string verbatim so the
  // UI shows readable English instead of an empty slot or a key fragment.
  return trimmed;
}
